import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Title } from 'react-admin';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { API_URL } from 'shared/constants/api-url';
import { httpClient } from 'shared/services/http-client';

type ContentStatus = 'draft' | 'published';

type MaterialItem = {
  id: string;
  files?: SavedMaterialFile[];
  name: string;
  description: string | null;
  status: ContentStatus;
  companyId: string;
  userIds?: string[];
  createdAt: string;
  updatedAt: string | null;
};

type PaginatedResponse<T> = {
  data: T[];
  total: number;
  page: number;
};

type CompanyItem = {
  id: string;
  name: string;
};

type PatientItem = {
  id: string;
  userId: string;
  firstname: string;
  lastname: string;
  email?: string;
};

type UploadedMaterialFile = {
  key: string;
  url: string;
};

type SavedMaterialFile = {
  name: string;
  url: string;
};

type MaterialFile = {
  id: string;
  name: string;
  url?: string;
  file?: File;
};

type MaterialForm = {
  id?: string;
  files: MaterialFile[];
  name: string;
  description: string;
  status: ContentStatus;
  companyId: string;
  userIds: string[];
};

const DEFAULT_FORM: MaterialForm = {
  files: [],
  name: '',
  description: '',
  status: 'published',
  companyId: '',
  userIds: [],
};

const PAGE_SIZE = 12;
const MAX_FILES = 10;
const MAX_USERS = 10;
const ACCEPTED_FILES = '.pdf,.doc,.docx';

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const patientLabel = (patient: PatientItem) => {
  const name = [patient.lastname, patient.firstname].filter(Boolean).join(' ');
  return [name, patient.email].filter(Boolean).join(' — ') || patient.userId;
};

const uploadMaterialFile = async (file: File) => {
  const body = new FormData();
  body.append('file', file);

  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.b2bMaterialUpload}`,
    {
      method: 'POST',
      body,
    },
  );
  const uploaded = response.json as UploadedMaterialFile;

  if (!uploaded.url) {
    throw new Error('Backend не вернул ссылку на файл');
  }

  return uploaded.url;
};

const B2BMaterials = () => {
  const [items, setItems] = useState<MaterialItem[]>([]);
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [patients, setPatients] = useState<PatientItem[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState<MaterialForm>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const selectedPatients = useMemo(() => {
    const byId = new Map(patients.map((patient) => [patient.userId, patient]));

    return form.userIds.map(
      (userId) =>
        byId.get(userId) || {
          id: userId,
          userId,
          firstname: '',
          lastname: '',
          email: userId,
        },
    );
  }, [form.userIds, patients]);

  const loadList = useCallback(async (nextPage: number) => {
    const safePage = nextPage;
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: String(safePage),
        limit: String(PAGE_SIZE),
      });
      const response = await httpClient(
        `${API_BASE_URL_ADMIN}${API_URL.b2bMaterials}?${params.toString()}`,
      );
      const json = response.json as PaginatedResponse<MaterialItem>;
      setItems(json.data || []);
      setTotal(json.total || 0);
      setPage(json.page || safePage);
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки материалов');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await httpClient(
        `${API_BASE_URL_ADMIN}${API_URL.companies}?limit=1000&page=1`,
      );
      const json = response.json as PaginatedResponse<CompanyItem>;
      setCompanies(json.data || []);
    } catch {
      setCompanies([]);
    }
  };

  const loadPatients = useCallback(async () => {
    if (!form.companyId) {
      setPatients([]);
      return;
    }

    const params = new URLSearchParams({
      page: '1',
      limit: form.id && !patientSearch.trim() ? '1000' : '20',
    });

    if (patientSearch.trim()) {
      params.set('search', patientSearch.trim());
    }

    try {
      const response = await httpClient(
        `${API_BASE_URL_ADMIN}${API_URL.companyPatients(
          form.companyId,
        )}?${params.toString()}`,
      );
      const json = response.json as PaginatedResponse<PatientItem>;
      setPatients(json.data || []);
    } catch {
      setPatients([]);
    }
  }, [form.companyId, form.id, patientSearch]);

  useEffect(() => {
    loadList(1);
    loadCompanies();
  }, [loadList]);

  useEffect(() => {
    if (!isDialogOpen) return;

    const timeout = window.setTimeout(loadPatients, 300);
    return () => window.clearTimeout(timeout);
  }, [isDialogOpen, loadPatients]);

  const openCreate = () => {
    loadCompanies();
    setForm(DEFAULT_FORM);
    setPatients([]);
    setPatientSearch('');
    setFormError(null);
    setIsDialogOpen(true);
  };

  const openEdit = (item: MaterialItem) => {
    loadCompanies();
    setForm({
      id: item.id,
      files: (item.files || []).map((file) => ({
        id: file.url,
        ...file,
      })),
      name: item.name,
      description: item.description || '',
      status: item.status,
      companyId: item.companyId,
      userIds: item.userIds || [],
    });
    setPatients([]);
    setPatientSearch('');
    setFormError(null);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    if (isSaving) return;
    setIsDialogOpen(false);
    setFormError(null);
  };

  const addFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (!files.length) return;

    if (form.files.length + files.length > MAX_FILES) {
      setFormError(`Можно загрузить не больше ${MAX_FILES} файлов`);
      return;
    }

    setFormError(null);
    const addedAt = Date.now();
    setForm((prev) => ({
      ...prev,
      files: [
        ...prev.files,
        ...files.map((file, index) => ({
          id: `${file.name}-${file.size}-${file.lastModified}-${addedAt}-${index}`,
          name: file.name,
          file,
        })),
      ],
    }));
  };

  const validate = () => {
    if (form.files.length === 0) return 'Загрузите хотя бы один файл';
    if (form.files.length > MAX_FILES)
      return `Можно загрузить не больше ${MAX_FILES} файлов`;
    if (form.files.some((file) => !file.name.trim()))
      return 'Укажите название для каждого файла';
    if (form.files.some((file) => !file.url && !file.file))
      return 'Файл не выбран';
    if (!form.name.trim()) return 'Введите название';
    if (!form.description.trim()) return 'Введите описание';
    if (!form.companyId) return 'Выберите B2B компанию';
    if (form.userIds.length > MAX_USERS) {
      return `Можно выбрать не больше ${MAX_USERS} пользователей`;
    }
    return null;
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const savedFiles: SavedMaterialFile[] = [];

      for (const file of form.files) {
        savedFiles.push({
          name: file.name.trim(),
          url: file.url || (await uploadMaterialFile(file.file!)),
        });
      }

      setForm((prev) => ({
        ...prev,
        files: savedFiles.map((file) => ({
          id: file.url,
          ...file,
        })),
      }));

      await httpClient(`${API_BASE_URL_ADMIN}${API_URL.b2bMaterials}`, {
        method: 'POST',
        body: JSON.stringify({
          id: form.id,
          files: savedFiles,
          name: form.name.trim(),
          description: form.description.trim(),
          status: form.status,
          companyId: form.companyId,
          userIds: form.userIds,
        }),
        headers: new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      });

      await loadList(form.id ? page : 1);
      setIsDialogOpen(false);
      setForm(DEFAULT_FORM);
    } catch (e: any) {
      setFormError(e?.message || 'Ошибка сохранения');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteItem = async () => {
    if (!deleteId) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await httpClient(
        `${API_BASE_URL_ADMIN}${API_URL.b2bMaterialById(deleteId)}`,
        {
          method: 'DELETE',
          headers: new Headers({ Accept: 'application/json' }),
        },
      );
      const nextPage = items.length === 1 && page > 1 ? page - 1 : page;
      await loadList(nextPage);
      setDeleteId(null);
    } catch (e: any) {
      setDeleteError(e?.message || 'Ошибка удаления');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="B2B материалы" />

      <Card>
        <CardHeader
          title="B2B материалы"
          action={
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => loadList(page)}
                disabled={isLoading}
              >
                Обновить
              </Button>
              <Button variant="contained" onClick={openCreate}>
                Создать
              </Button>
            </Stack>
          }
        />

        {isLoading ? (
          <LinearProgress color="primary" />
        ) : (
          <LinearProgress
            variant="determinate"
            value={0}
            sx={{ visibility: 'hidden' }}
          />
        )}

        <CardContent>
          {error && (
            <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {items.length === 0 && !isLoading ? (
            <Typography>Материалов нет</Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Название</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Файлы</TableCell>
                    <TableCell>Пользователи</TableCell>
                    <TableCell>Дата создания</TableCell>
                    <TableCell align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            item.status === 'published'
                              ? 'Опубликовано'
                              : 'Черновик'
                          }
                          color={
                            item.status === 'published' ? 'success' : 'default'
                          }
                          variant={
                            item.status === 'published' ? 'filled' : 'outlined'
                          }
                        />
                      </TableCell>
                      <TableCell>{item.files?.length || 0}</TableCell>
                      <TableCell>{item.userIds?.length || 'Все'}</TableCell>
                      <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openEdit(item)}>
                          Редактировать
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={isDeleting}
                          onClick={() => {
                            setDeleteId(item.id);
                            setDeleteError(null);
                          }}
                        >
                          Удалить
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={total}
                page={Math.max(0, page - 1)}
                onPageChange={(_, nextPage0) => {
                  const nextPage = nextPage0 + 1;
                  setPage(nextPage);
                  loadList(nextPage);
                }}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                onRowsPerPageChange={() => {}}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {form.id ? 'Редактирование материала' : 'Создание материала'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <Stack spacing={1}>
              <Typography variant="subtitle2">Файлы *</Typography>
              <Button variant="outlined" component="label" disabled={isSaving}>
                Добавить PDF/DOC
                <input
                  hidden
                  multiple
                  type="file"
                  accept={ACCEPTED_FILES}
                  onChange={addFiles}
                />
              </Button>
            </Stack>

            {form.files.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
              >
                {form.files.map((file) => (
                  <Stack
                    key={file.id}
                    direction="row"
                    spacing={1}
                    sx={{ width: '100%', alignItems: 'center' }}
                  >
                    <TextField
                      label="Название файла"
                      required
                      value={file.name}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          files: prev.files.map((item) =>
                            item.id === file.id
                              ? { ...item, name: e.target.value }
                              : item,
                          ),
                        }))
                      }
                      fullWidth
                    />
                    <Chip
                      label={
                        file.file?.name || file.url?.split('/').pop() || 'Файл'
                      }
                      onDelete={() =>
                        setForm((prev) => ({
                          ...prev,
                          files: prev.files.filter(
                            (item) => item.id !== file.id,
                          ),
                        }))
                      }
                    />
                  </Stack>
                ))}
              </Stack>
            )}

            <TextField
              label="Название"
              required
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Описание"
              required
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              fullWidth
              multiline
              minRows={3}
            />

            <FormControl fullWidth required>
              <InputLabel id="material-status-label">Статус</InputLabel>
              <Select
                labelId="material-status-label"
                label="Статус"
                value={form.status}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    status: e.target.value as ContentStatus,
                  }))
                }
              >
                <MenuItem value="draft">Черновик</MenuItem>
                <MenuItem value="published">Опубликовано</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              options={companies}
              value={
                companies.find((company) => company.id === form.companyId) ||
                null
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              onChange={(_, value) => {
                setForm((prev) => ({
                  ...prev,
                  companyId: value?.id || '',
                  userIds: [],
                }));
                setPatients([]);
                setPatientSearch('');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="B2B компания"
                  required
                  placeholder="Выберите"
                />
              )}
            />

            <Autocomplete
              multiple
              options={patients}
              value={selectedPatients}
              disabled={!form.companyId}
              inputValue={patientSearch}
              isOptionEqualToValue={(option, value) =>
                option.userId === value.userId
              }
              getOptionLabel={patientLabel}
              onInputChange={(_, value) => setPatientSearch(value)}
              onChange={(_, value) =>
                setForm((prev) => ({
                  ...prev,
                  userIds: value
                    .slice(0, MAX_USERS)
                    .map((patient) => patient.userId),
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="B2B пользователи"
                  helperText="Не выбирайте пользователей, если материал должен быть доступен всей компании"
                  placeholder={
                    form.companyId
                      ? 'Поиск по имени или email'
                      : 'Сначала выберите компанию'
                  }
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isSaving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={save} disabled={isSaving}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Удалить материал?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <Typography>
              Запись будет удалена, файлы в storage останутся.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={isDeleting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={deleteItem}
            disabled={isDeleting}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default B2BMaterials;
